import './Admin_Pickup.css'
import { useQuery } from '@tanstack/react-query'
import { Table, Loader, Alert, Badge, Button } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useState, useEffect } from 'react'

import { bookingApi } from '../api/booking.api'
import { BookingResponse } from '../types/bookingTypes'
import OrderDetailModal from './orderDetailModal'
import { formatDate, formatPhoneNumber, getBadgeStyle, getHomeButtonStyle } from '../utils'
import { Link } from 'react-router-dom'

export default function Admin_Pickup() {
  const [opened, { open, close }] = useDisclosure(false)
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState<boolean>(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const { data, isError, isFetching, isLoading } = useQuery({
    queryKey: ['bookings'],
    queryFn: async () => bookingApi.getAll(),
  })

  function onRowClick(booking: BookingResponse) {
    setSelectedBookingId(booking._id)
    open()
  }

  function onClose() {
    close()
    setSelectedBookingId(null)
  }

  if (isLoading)
    return (
      <div className='loading-container'>
        <Loader size='lg' />
      </div>
    )

  if (isError)
    return (
      <div className='error-container'>
        <Alert color='red' title='Error'>
          Failed to load bookings
        </Alert>
      </div>
    )

  const bookings = data || []

  return (
    <div className='admin-container'>
      <div className='admin-header'>
        <h1 className='admin-title'>Booking Management</h1>
        <Link to='/'>
          <Button style={getHomeButtonStyle()}>HOME</Button>
        </Link>
      </div>

      <div className='admin-table-container'>
        {bookings.length === 0 ? (
          <div className='empty-province'>
            <div className='empty-province-icon'>📋</div>
            <div className='empty-province-text'>No bookings found</div>
            <div className='empty-province-subtext'>There are currently no bookings in the system</div>
          </div>
        ) : (
          <Table className='admin-table'>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order#</Table.Th>
                {isMobile && <Table.Th>Status</Table.Th>}
                <Table.Th>CreatedAt</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Phone</Table.Th>
                <Table.Th>Email</Table.Th>
                {!isMobile && <Table.Th>Status</Table.Th>}
                <Table.Th>Pickup Date</Table.Th>
                <Table.Th>Pickup Time</Table.Th>
                <Table.Th>Address</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {bookings.map(booking => (
                <Table.Tr key={booking._id} className='row-clickable' onClick={() => onRowClick(booking)}>
                  <Table.Td>{booking.orderNumber}</Table.Td>
                  {isMobile && (
                    <Table.Td>
                      <Badge style={{ ...getBadgeStyle(booking.status), fontSize: '12px' }} variant='filled' size='xs'>
                        {booking.status}
                      </Badge>
                    </Table.Td>
                  )}
                  <Table.Td>{new Date(booking.createdAt).toLocaleDateString()}</Table.Td>
                  <Table.Td>
                    {booking.firstName} {booking.lastName}
                  </Table.Td>
                  <Table.Td>{formatPhoneNumber(booking.phoneNumber)}</Table.Td>
                  <Table.Td>{booking.email}</Table.Td>
                  {!isMobile && (
                    <Table.Td>
                      <Badge style={getBadgeStyle(booking.status)} variant='filled' size='md'>
                        {booking.status}
                      </Badge>
                    </Table.Td>
                  )}
                  <Table.Td>{new Date(booking.pickupDate).toLocaleDateString()}</Table.Td>
                  <Table.Td>{booking.pickupTime}</Table.Td>
                  <Table.Td>
                    {booking.address.streetAddress} {booking.address.city} {booking.address.province}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </div>
      <OrderDetailModal bookingId={selectedBookingId} opened={opened} onClose={onClose} />
    </div>
  )
}
